// Phase 2: Date Range Picker Component
// Quick presets + custom date selection

import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import type { DateRange } from '../../../shared/types/reports';

interface DateRangePickerProps {
  value: DateRange;
  onChange: (dateRange: DateRange) => void;
}

const PRESETS = [
  { label: 'Today', value: 'today' as const },
  { label: 'Last 7 days', value: 'last_7_days' as const },
  { label: 'Last 30 days', value: 'last_30_days' as const },
  { label: 'Last 90 days', value: 'last_90_days' as const },
  { label: 'Current Month', value: 'current_month' as const },
  { label: 'Last Month', value: 'last_month' as const },
  { label: 'Custom', value: 'custom' as const },
];

function calculateDateRange(preset: string): { start: string; end: string } {
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  
  switch (preset) {
    case 'today':
      return { start: today, end: today };
    case 'last_7_days':
      return {
        start: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: today,
      };
    case 'last_30_days':
      return {
        start: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: today,
      };
    case 'last_90_days':
      return {
        start: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: today,
      };
    case 'current_month':
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
        end: today,
      };
    case 'last_month': {
      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      return {
        start: lastMonthDate.toISOString().split('T')[0],
        end: lastMonthEnd.toISOString().split('T')[0],
      };
    }
    default:
      return { start: today, end: today };
  }
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({ value, onChange }) => {
  const [showCustom, setShowCustom] = useState(value.preset === 'custom');

  const handlePresetChange = (preset: string) => {
    if (preset === 'custom') {
      setShowCustom(true);
      onChange({ ...value, preset: 'custom' });
    } else {
      setShowCustom(false);
      const range = calculateDateRange(preset);
      onChange({
        start: range.start,
        end: range.end,
        preset: preset as any,
      });
    }
  };

  const handleCustomDateChange = (field: 'start' | 'end', date: string) => {
    onChange({
      ...value,
      [field]: date,
      preset: 'custom',
    });
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Date Range
      </label>

      {/* Preset Dropdown */}
      <div className="relative">
        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
        <select
          value={value.preset || 'custom'}
          onChange={(e) => handlePresetChange(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg
            bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
            appearance-none cursor-pointer"
        >
          {PRESETS.map((preset) => (
            <option key={preset.value} value={preset.value}>
              {preset.label}
            </option>
          ))}
        </select>
      </div>

      {/* Custom Date Inputs */}
      {showCustom && (
        <div className="grid grid-cols-2 gap-3 pt-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={value.start}
              onChange={(e) => handleCustomDateChange('start', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={value.end}
              onChange={(e) => handleCustomDateChange('end', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      )}

      {/* Date Range Display */}
      {!showCustom && value.start && value.end && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {new Date(value.start).toLocaleDateString()} - {new Date(value.end).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};
